import type { EmbeddedCode } from '@jms/model';
import type { EntityBoardColumnOverrides } from '@workbuddy/ui/v2/blocks/entity.board';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';

const tokens = DesignSystem.tokens;

// Straight from the tokens: the SOR relation mount carries no `.sor-board` class for a bridge to hang on.
const statusInset = tokens.spacing.threeXl;
const glyphCell = { paddingLeft: tokens.spacing.xs, paddingRight: tokens.spacing.xs };

/**
 * SOR column sizing. This layout replaces the Items column function on the Job Item board.
 *
 * SOR must fit its host on open with no horizontal scroll: 982px at 1440 inside the Job manage
 * surface, beside the 300px rail. Description is the only column that wraps, so it alone flexes; the
 * money trio and Status size to their content (`autoSizeStrategy` on the manifest); everything else is
 * fixed at its content floor. No `maxWidth`: the layout on open is what matters, then the user drags
 * freely — the board does not persist widths.
 *
 * No sizing for the standalone Ext pair or for Location: no view carries those as columns any more.
 * The Ext figures are never materialised per item, so such a column only ever read "Not Set"; both
 * are now composed into a cell instead — the money cell stacks the extension over the unit price, and
 * the Item cell stacks location under the name (which is also where `items.hideLocation` applies).
 */
export function resolveJobItemSorColumnOverrides(): EntityBoardColumnOverrides<EmbeddedCode> {

  // Budget at 1440: chrome 56 (checkbox, overlay spacer) + fixed 315 + trio at its widest 270 +
  // Status at its widest 161 + name 120 + description 60 = 982 ≤ 982. The test on this file guards it.
  //
  // autoHeight is opt-in per column, never board-wide: AG gives an auto-height cell no height, so its
  // content stops centring and the column loses virtualisation. Each board marks the one column that
  // must define its row height — description here, status on Items Pro.
  return {
    byField: {
      'workOrders.codes.completion': { width: 28, minWidth: 28, flex: 0, cellStyle: glyphCell },
      // Identifier: the framework pins it at min === max, so the max is cleared for flex to grow it.
      'workOrders.codes.name': { flex: 1, minWidth: 120, maxWidth: undefined },
      'workOrders.codes.description': { flex: 4, minWidth: 60, wrapText: true, autoHeight: true },
      // 70 is only the base before autosize measures the content.
      'workOrders.codes.quantity': { flex: 0, width: 70 },
      'workOrders.codes.buyPrice': { flex: 0, width: 70 },
      'workOrders.codes.sellPrice': { flex: 0, width: 70 },
      // Status column: the framework pins it at min === max; both cleared so autosize and drag are free.
      'workOrders.codes.itemStatus': { flex: 0, minWidth: undefined, maxWidth: undefined, cellStyle: { paddingLeft: statusInset }, headerStyle: { paddingLeft: statusInset } },
      // Photos, tasks, finance and the icon-only evidence badge (26px plus its gap).
      'workOrders.codes.indicators': { flex: 0, width: 175, minWidth: 175 },
      // Two buttons here (pen, expand); the grid's own padding pushed the pen onto the indicators cell.
      w_row_editor: { width: 72, minWidth: 72, maxWidth: 72, flex: 0, cellStyle: { padding: '0' } },
      w_commands: { width: 40, minWidth: 40, maxWidth: 40 },
    },
  };
}

export type JobItemColumnOptions = {
  readonly compact?: boolean;
  /** The mount is one stage's Items tab, so a Stage Number column would repeat one value down the grid. */
  readonly stageLevel?: boolean;
  /** The portal drops the status badges and stacks the note under the description, so description measures the row. */
  readonly contractor?: boolean;
};

/** Items column sizing, including the compact version of that layout. */
export function resolveJobItemColumnOverrides(options: JobItemColumnOptions = {}): EntityBoardColumnOverrides<EmbeddedCode> {

  // Hidden here rather than removed from the view: the view is tenant data shared by the Job and Stage
  // mounts, and the override outranks the view's own visibility (applyEntityBoardColumnOverrides).
  const stageNumberColOptions = options.stageLevel ? { hide: true } : {};
  // An auto-height cell gets no height, so the pill it holds sits at the top of a taller row.
  const statusHeightOptions = options.contractor ? { autoHeight: false } : { autoHeight: true };
  const descriptionHeightOptions = options.contractor ? { wrapText: true, autoHeight: true } : {};

  if (options.compact) {
    return {
      byField: {
        'workOrders.codes.name': { width: 120, minWidth: 110, maxWidth: 160 },
        'workOrders.codes.description': { width: 100, minWidth: 80, flex: 1, ...descriptionHeightOptions },
        'workOrders.workOrderNumber': { width: 110, minWidth: 105, maxWidth: 130, ...stageNumberColOptions },
        'workOrders.codes.quantity': { width: 65, minWidth: 60, maxWidth: 80 },
        'workOrders.codes.buyPrice': { width: 70, minWidth: 65, maxWidth: 85 },
        'workOrders.codes.sellPrice': { width: 70, minWidth: 65, maxWidth: 85 },
        // The measured column: it stacks the status pill over the O/B/I link badges, which overflows
        // the default row. SOR drops those badges (sorMode) and measures description instead.
        'workOrders.codes.itemStatus': { width: 140, minWidth: 140, maxWidth: 140, ...statusHeightOptions },
        w_row_editor: { width: 44, minWidth: 44, maxWidth: 44 },
        w_commands: { width: 44, minWidth: 44, maxWidth: 44 },
      },
    };
  }

  return {
    byField: {
      'workOrders.codes.name': { width: 140, minWidth: 120, maxWidth: 180 },
      'workOrders.codes.description': { width: 170, minWidth: 100, flex: 1, ...descriptionHeightOptions },
      'workOrders.workOrderNumber': { width: 115, minWidth: 110, maxWidth: 140, ...stageNumberColOptions },
      'workOrders.codes.quantity': { width: 70, minWidth: 65, maxWidth: 90 },
      'workOrders.codes.buyPrice': { width: 75, minWidth: 70, maxWidth: 90 },
      'workOrders.codes.sellPrice': { width: 75, minWidth: 70, maxWidth: 90 },
      // Measured column — see the compact branch.
      'workOrders.codes.itemStatus': { width: 140, minWidth: 140, maxWidth: 140, ...statusHeightOptions },
      w_row_editor: { width: 44, minWidth: 44, maxWidth: 44 },
      w_commands: { width: 44, minWidth: 44, maxWidth: 44 },
    },
  };
}
